import React, { useRef, useEffect } from 'react';
import { Gift, Users, Droplet, Sparkles, Phone } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { specialDeals } from '../mockData';

export const SpecialOffersPage = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      video.play().catch((error) => {
        console.log('Autoplay prevented:', error);
      });
    }
  }, []);

  return (
    <div className="pt-20">
      {/* Hero Section with Video */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
            poster="https://customer-assets.emergentagent.com/job_roof-booking-hub/artifacts/zrqyrs2h_b1af1cf4-190b-4caf-945d-73d8591fc9b9.jpeg"
          >
            <source src="/dezire_montage_v2.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/95 via-gray-800/90 to-gray-900/95"></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl z-10"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl z-10"></div>

        <div className="container mx-auto px-4 relative z-20">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center space-x-2 mb-4">
              <Sparkles className="w-6 h-6 text-primary" />
              <Badge className="bg-primary/20 text-primary border-primary/30">Limited Time Offers</Badge>
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Exclusive <span className="text-primary">Special Deals</span>
            </h1>
            <p className="text-xl text-gray-300">
              Take advantage of our current promotions and save big on your metal roofing project
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
            {/* Deal 1: Referral Bonus */}
            <Card className="bg-white/5 backdrop-blur-lg border-white/10 card-hover">
              <CardHeader>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <Badge className="bg-primary text-white text-lg px-4 py-2">
                    {specialDeals[0].value}
                  </Badge>
                </div>
                <CardTitle className="text-2xl font-bold text-white">
                  {specialDeals[0].title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300 text-base mb-6">
                  {specialDeals[0].description}
                </CardDescription>
                <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-400">
                    <strong className="text-white">Terms:</strong> {specialDeals[0].terms}
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/contact')}
                  className="w-full bg-primary hover:bg-accent text-white"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Get a Free Quote
                </Button>
              </CardContent>
            </Card>

            {/* Deal 2: Free Downpipes */}
            <Card className="bg-white/5 backdrop-blur-lg border-white/10 card-hover">
              <CardHeader>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center">
                    <Droplet className="w-8 h-8 text-white" />
                  </div>
                  <Badge className="bg-primary text-white text-lg px-4 py-2">
                    {specialDeals[1].value}
                  </Badge>
                </div>
                <CardTitle className="text-2xl font-bold text-white">
                  {specialDeals[1].title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300 text-base mb-6">
                  {specialDeals[1].description}
                </CardDescription>
                <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-400">
                    <strong className="text-white">Terms:</strong> {specialDeals[1].terms}
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/contact')}
                  className="w-full bg-primary hover:bg-accent text-white"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Claim Offer
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <p className="text-gray-300 text-lg mb-6">
              Don't miss out on these incredible savings! Contact us today to take advantage of these limited-time offers.
            </p>
            <Button
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-primary hover:bg-accent text-white text-lg px-8 py-6"
            >
              <Phone className="w-5 h-5 mr-2" />
              Book Your Free Inspection Now
            </Button>
          </div>
        </div>
      </section>

      {/* Additional Benefits Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Why Take Advantage of These Offers?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-3">Save Money</h3>
                <p className="text-gray-600">
                  Our special offers provide significant savings on premium metal roofing services without compromising on quality.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-3">Limited Time</h3>
                <p className="text-gray-600">
                  These exclusive deals won't last forever. Book now to secure your discounted rate and priority scheduling.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-3">Quality Guaranteed</h3>
                <p className="text-gray-600">
                  All promotional work includes our standard warranties and is performed by our licensed, experienced team.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-3">Easy Process</h3>
                <p className="text-gray-600">
                  Simply mention the offer when you book your free inspection. We'll handle everything from there.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
